import { inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap, Observable, combineLatest, map, withLatestFrom, filter } from 'rxjs';
import { OptionsListApiService } from '../../collections/services/options-list-api.service';
import { PageResult } from 'src/app/shared/models/pageResult';
import { FilterItemList } from 'src/app/shared/models/filter.model';
import { ManipulationsToolbarStore } from 'src/app/store/manipulations-toolbar-store';
import { GlobalStore } from 'src/app/store/global-store';
import { toSignal } from '@angular/core/rxjs-interop';
import { OptionsList, OptionsListGridItem } from 'src/app/models/optionsList.model';
import { TostesStore } from 'src/app/store/tostes-store';
import { TostPositionEnum, TostTypeEnum } from 'src/app/shared/models/tostes.model';

export interface CollectionListsState {
  readonly collection: PageResult<OptionsListGridItem>;
  readonly savedListExtra: PageResult<OptionsListGridItem>;
  filterList: FilterItemList[];
  readonly loading: boolean;
  readonly loadingExtra: boolean;
  isRequestOfExtaraWasEmpty: boolean;
  error?: string;
}

export class CollectionListsStore extends ComponentStore<CollectionListsState> {

  #optionsListApiService = inject(OptionsListApiService);
  #manipulationsToolbarStore = inject(ManipulationsToolbarStore);
  #globalStore = inject(GlobalStore);
  #tostesStore = inject(TostesStore);

  constructor() {
    super({
      collection: { result: [], totals: 0 },
      savedListExtra: { result: [], totals: 0 },
      filterList: [],
      loading: false,
      loadingExtra: false,
      isRequestOfExtaraWasEmpty: true
    });
    this.#manipulationsToolbarStore.selectedFilter$.subscribe(x => this.getSavedLists());
    this.#manipulationsToolbarStore.isDisplayNotActiveItems$.subscribe(x => this.getSavedLists());
    // this.loadFilterList();
    this.getSavedLists();
    this.#globalStore.refreshLists$.subscribe(ev => {
      this.getSavedLists(true);
    });
  }
  readonly collectionLists$ = this.select(({ collection }) => collection);
  readonly savedListsSig$ = toSignal(this.collectionLists$);
  readonly savedListsExtra$ = this.select((x) => x.savedListExtra);
  readonly savedListsExtraSig$ = toSignal(this.savedListsExtra$);
  readonly totals$ = this.select((x) => x.collection.totals);
  readonly isEmpty$ = this.select((x) => x.collection.isEmpty);
  // readonly selectedFilter$ = this.select((x) => x.selectedFilter);
  readonly filterList$ = this.select((x) => x.filterList);
  readonly loadingExtra$ = this.select((x) => x.loadingExtra);
  readonly loadingExtraSig$ = toSignal(this.loadingExtra$);
  readonly loading$ = this.select((x) => x.loading);
  readonly isRequestOfExtaraWasEmptySig$ = toSignal(this.select((x) => x.isRequestOfExtaraWasEmpty))

  // readonly currentListEdit$ = this.select(({ currentListEdit }) => currentListEdit);

  readonly getSavedLists = this.effect<void | boolean>(
    pipe(
      tap(() => this.patchState({ loading: true })),
      tap((ev) => {
        if (ev) {
          this.#manipulationsToolbarStore.resetPage()
        }
      }),
      withLatestFrom(this.#manipulationsToolbarStore.selectedFilter$, this.#manipulationsToolbarStore.page$, this.#manipulationsToolbarStore.isDisplayNotActiveItems$),
      filter(([event, selectedFilter, page, isDisplayNotActiveItems]) => selectedFilter != null),
      switchMap(([event, selectedFilter, page, isDisplayNotActiveItems]) => this.#optionsListApiService.getSavedList(event ? 1 : page, { ...selectedFilter!, isActive: !isDisplayNotActiveItems })),
      tap((collection: PageResult<OptionsListGridItem>) => this.patchState({ collection })),
      tap(() => {
        this.patchState({ loading: false }),
          this.loadSavedListForExtra()
      }),
    )
  );
  readonly getSavedListsRefresh = this.effect<void | boolean>(
    pipe(
      tap(() => this.patchState({ loading: true })),
      tap((ev) => {
        this.#manipulationsToolbarStore.resetPage()
      }),
      withLatestFrom(this.#manipulationsToolbarStore.selectedFilter$, this.#manipulationsToolbarStore.page$, this.#manipulationsToolbarStore.isDisplayNotActiveItems$),
      filter(([_, selectedFilter, page, isDisplayNotActiveItems]) => selectedFilter != null),
      switchMap(([isRefresh, selectedFilter, page, isDisplayNotActiveItems]) => this.#optionsListApiService.getSavedList(isRefresh ? 1 : page, { ...selectedFilter!, isActive: !isDisplayNotActiveItems })),
      tap((collection: PageResult<OptionsListGridItem>) => this.patchState({ collection })),
      tap(() => {
        this.patchState({ loading: false }),
          this.loadSavedListForExtra()
      }),
    )
  );
  readonly loadSavedListForExtra = this.effect<void>(
    pipe(
      tap(() => this.patchState({ loadingExtra: true })),
      tap(() => this.#manipulationsToolbarStore.updatePage()),
      withLatestFrom(this.#manipulationsToolbarStore.selectedFilter$, this.#manipulationsToolbarStore.page$, this.#manipulationsToolbarStore.isDisplayNotActiveItems$),
      filter(([_, selectedFilter, page, isDisplayNotActiveItems]) => selectedFilter != null),
      switchMap(([_, selectedFilter, page, isDisplayNotActiveItems]) => this.#optionsListApiService.getSavedList(page, { ...selectedFilter!, isActive: !isDisplayNotActiveItems })
      ),
      tap((result: PageResult<OptionsListGridItem>) => {
        this.patchState({
          savedListExtra: result,
        })
      }),
      tap(() => this.patchState({ loadingExtra: false }))
    )
  );
  readonly changeListStatus = this.effect<OptionsListGridItem>(
    pipe(
      tap((i) => this.patchState({ loading: true })),
      map(list => {
        const guid = this.#tostesStore.generateGuid();
        this.#tostesStore.addTost({ guid, message: { en: "Changes saved list status....", he: "משנה סטטוס רשימה..." }, position: TostPositionEnum.BottomLeft, type: TostTypeEnum.LOAD });
        return { list, guid };
      }),
      switchMap((props) =>
        this.#optionsListApiService.ChangeListStatusById(props.list.guid).pipe(
          tapResponse(
            () => {
              this.#tostesStore.removeTostByGuid(props.guid)
              var message = { en: `The ${props.list.name} list is active`, he: ` הרשימה ${props.list.name} פעילה` };
              if (props.list.isActive) {
                message = { en: `The ${props.list.name} list is inactive`, he: ` הרשימה ${props.list.name} לא פעילה` };
              }
              this.#tostesStore.addTost({ guid: "", message, type: TostTypeEnum.SUCCESS, seconds: 5 });
              // this.#manipulationsToolbarStore.resetPage();
              this.getSavedListsRefresh();
              this.patchState({ loading: false })
            },
            (error: Error) => {
              this.#tostesStore.removeTostByGuid(props.guid)
              this.#tostesStore.addTost({ guid: "", message: { en: "An error occurred while updating the saved list status", he: "אירעה שגיאה בעת עדכון סטטוס רשימה" }, type: TostTypeEnum.ERROR, seconds: 5 });
              this.patchState({ error: error?.message, loading: false })

            })
        )
      )
    )
  );

  readonly updateList = this.effect<OptionsList>(
    pipe(
      map(list => {
        const guid = this.#tostesStore.generateGuid();
        this.#tostesStore.addTost({ guid, message: { en: "Updating list...", he: "מעדכן רשימה..." }, position: TostPositionEnum.BottomLeft, type: TostTypeEnum.LOAD });
        return { list, guid };
      }),
      // tap((id) => this.patchState({ currentListEdit: id })),
      switchMap((props) =>
        this.#optionsListApiService.updateList(props.list).pipe(
          tapResponse(
            (list) => {
              this.#tostesStore.removeTostByGuid(props.guid)
              this.#tostesStore.addTost({ guid: "", message: { en: `The ${props.list.name} list has been updated`, he: ` הרשימה ${props.list.name} התעדכנה` }, type: TostTypeEnum.SUCCESS, seconds: 5 });
              this.getSavedListsRefresh();
            },
            (error: Error) => {
              this.#tostesStore.removeTostByGuid(props.guid)
              this.#tostesStore.addTost({ guid: "", message: { en: "An error occurred while updating the list", he: "אירעה שגיאה בעת עדכון הרשימה" }, type: TostTypeEnum.ERROR, seconds: 5 });
              this.patchState({ error: error?.message, loading: false })
            })
        )
      )
    )
  );

  readonly updateListToAdd = this.updater((state, list: OptionsList) => ({
    ...state,
    currentListEdit: list
  }));
  readonly saveListEdit = this.effect<OptionsList>(
    pipe(
      switchMap((list) =>
        this.#optionsListApiService.updateList(list).pipe(
          tapResponse(
            (list) => {
              this.getSavedListsRefresh();
            },
            (error: Error) =>
              this.patchState({ error: error?.message, loading: false })
          )
        )
      )
    )
  );
  readonly createList = this.effect<OptionsList>(
    pipe(
      switchMap((list) =>
        this.#optionsListApiService.createNewList(list).pipe(
          tapResponse(
            (list) => {
              this.getSavedListsRefresh();
              this.#globalStore.fetchOptionLists();
            },
            (error: Error) =>
              this.patchState({ error: error?.message, loading: false })
          )
        )
      )
    )
  );

  readonly duplicateList = this.effect<{ guid: string, name: string }>(
    pipe(
      switchMap((listDetails) =>
        this.#optionsListApiService.duplicateList(listDetails.guid, listDetails.name).pipe(
          tapResponse(
            (list) => {
              this.getSavedListsRefresh();
            },
            (error: Error) =>
              this.patchState({ error: error?.message, loading: false })
          )
        )
      )
    )
  );

  readonly updateSavedListWithExtra = this.updater((state) => (
    {
      ...state,
      isRequestOfExtaraWasEmpty: ((this.savedListsExtraSig$()?.result == undefined || this.savedListsExtraSig$()?.result.length == 0) && this.loadingExtraSig$()) ? true : false,
      collection: {
        result: [...(this.savedListsSig$()?.result || []), ...(this.savedListsExtraSig$()?.result || [])],
        totals: this.savedListsSig$()?.totals || 0
      },
      savedListExtra: { result: [], totals: 0 }
    }
  ));

  // readonly loadFilterList = this.effect<void>(
  //     pipe(
  //         switchMap(() => this.#optionsListApiService.getFilterItemList()
  //         ),

  //         tap((filterList: FilterItemList[]) => this.patchState({ filterList }))
  //     )
  // );
}